/**
 * Elección del mensaje que acompaña a cada momento.
 *
 * Determinista y sin estado: el mensaje depende solo del instante, de la fase y
 * (en preparativos) de lo cerca que esté la Navidad. Rota cada tres horas, así
 * que quien vuelve por la tarde no se encuentra exactamente la misma frase de
 * por la mañana, pero tampoco un texto distinto en cada recarga.
 */

import type { TrackerCopy } from './content/types'
import { formatTemplate } from './content'
import { getStopById } from './route'
import { proximityBucket, rotatingIndex } from './stats'
import type { TrackerState } from './types'

/** Horas UTC que se consideran «de madrugada» en el Polo Norte. */
const NIGHT_HOURS = new Set([0, 1, 2, 3, 4, 5])

function pick(options: readonly string[], nowMs: number): string {
  if (options.length === 0) return ''
  return options[rotatingIndex(nowMs, options.length)]
}

/** Frase de estado: qué está haciendo Papá Noel ahora mismo. */
export function resolveStatusMessage(state: TrackerState, copy: TrackerCopy): string {
  if (state.phase === 'preparing') {
    return pick(copy.status.preparing[proximityBucket(state)], state.nowMs)
  }
  if (state.phase === 'liftoff') {
    return pick(copy.status.liftoff, state.nowMs)
  }
  if (state.phase === 'completed') {
    return pick(copy.status.completed, state.nowMs)
  }

  const lastStop = state.lastStopId ? getStopById(state.lastStopId) : null
  if (!lastStop || lastStop.leg === 'north-pole') {
    // Acaba de despegar: todavía no hay ninguna ciudad de la que hablar.
    return formatTemplate(copy.status.flyingTo, {
      leg: copy.status.legLabel[state.currentLeg],
    })
  }
  return formatTemplate(pick(copy.status.traveling, state.nowMs), {
    city: copy.stops[lastStop.id],
  })
}

/** Actividad del taller. De madrugada manda la lista nocturna. */
export function resolveWorkshopActivity(state: TrackerState, copy: TrackerCopy): string {
  const isNight = NIGHT_HOURS.has(new Date(state.nowMs).getUTCHours())
  // Durante el viaje el Polo Norte está despierto pase lo que pase: no es noche
  // cualquiera, es *la* noche.
  const options =
    isNight && state.phase !== 'traveling' && state.phase !== 'liftoff'
      ? copy.workshop.night
      : copy.workshop.activities[state.phase]
  return pick(options, state.nowMs)
}

/**
 * Descripción textual del mapa para lectores de pantalla: dónde está el trineo,
 * de dónde viene y a dónde va.
 */
export function describeMap(state: TrackerState, copy: TrackerCopy): string {
  const parts = [resolveStatusMessage(state, copy)]
  if (state.nextStopId) {
    const next = getStopById(state.nextStopId)
    if (next) parts.push(`${copy.status.nextStop}: ${copy.stops[next.id]}.`)
  }
  return parts.join(' ')
}
